/**
 * Authentication: Entra ID, through MSAL.
 *
 * Authorization Code with PKCE against the FMM tenant, with a SPA public-client
 * registration requesting the backend's exposed API scope. Two rules from the
 * specification drive the shape of this module:
 *
 *   - **No token in localStorage.** MSAL's `sessionStorage` cache is used instead, and
 *     tokens are never copied anywhere else — the API client asks for one per request
 *     and MSAL renews it silently.
 *   - **`authDisabled` injects a fixed identity**, mirroring the backend's
 *     `AUTH_DISABLED`, so the SPA is usable before the Entra registration exists. It is
 *     forced off in a production build (see src/config.js).
 *
 * The three providers below expose the same five methods, so nothing above this file —
 * no store, no view, not the API client — deals with MSAL directly. Entra is the only
 * origin they talk to: no avatar is read from Microsoft Graph.
 */

import { config } from '@/config.js'

/**
 * @typedef {object} AuthProvider
 * @property {() => Promise<void>} initialize handles the redirect response, restores a session
 * @property {() => Promise<void>} signIn
 * @property {() => Promise<void>} signOut
 * @property {() => Promise<string|null>} getToken a valid access token, renewed silently
 * @property {() => ({username: string, name: string, upn: string}|null)} getAccount
 */

/** The identity injected when authentication is switched off locally. */
export const STUB_ACCOUNT = {
  username: 'dev.local',
  name: 'Local developer',
  upn: 'dev.local@francemediasmonde.com',
}

/**
 * @returns {AuthProvider}
 */
export function createStubProvider() {
  let signedIn = true
  return {
    async initialize() {},
    async signIn() {
      signedIn = true
    },
    async signOut() {
      signedIn = false
    },
    async getToken() {
      return signedIn ? 'stub-token' : null
    },
    getAccount() {
      return signedIn ? { ...STUB_ACCOUNT } : null
    },
  }
}

/** Set while a renewal redirect is in flight, so a failed one cannot loop. */
const REAUTH_KEY = 'previewapp.reauth'

/**
 * Entra ID, through MSAL.
 *
 * MSAL is imported dynamically: it is the largest dependency in the build, and the
 * stubbed local path never needs it.
 *
 * Renewal never uses MSAL's hidden iframe: it frames our own redirect page, which nginx
 * answers with `X-Frame-Options: DENY`, and it needs the Entra cookie in a third-party
 * context, which Firefox and Safari drop. Enabled, it stalls six seconds and throws
 * `monitor_window_timeout`. Refresh token redemption is a first-party fetch and works.
 *
 * @param {typeof config} cfg
 * @returns {AuthProvider}
 */
export function createMsalProvider(cfg = config) {
  /** @type {import('@azure/msal-browser').PublicClientApplication|null} */
  let msal = null
  let account = null
  let InteractionRequiredAuthError = null
  let BrowserAuthError = null
  let CacheLookupPolicy = null

  const scopes = cfg.entra.apiScope ? [cfg.entra.apiScope] : []

  async function ensureInstance() {
    if (msal) return msal
    const msalBrowser = await import('@azure/msal-browser')
    InteractionRequiredAuthError = msalBrowser.InteractionRequiredAuthError
    BrowserAuthError = msalBrowser.BrowserAuthError
    CacheLookupPolicy = msalBrowser.CacheLookupPolicy
    msal = new msalBrowser.PublicClientApplication({
      auth: {
        clientId: cfg.entra.clientId,
        // `authority` overrides the tenant entirely, for a sovereign cloud or B2C.
        authority:
          cfg.entra.authority || `https://login.microsoftonline.com/${cfg.entra.tenantId}`,
        redirectUri: cfg.entra.redirectUri || window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        // Session storage, never localStorage: the token dies with the tab.
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: { logLevel: msalBrowser.LogLevel.Error },
      },
    })
    await msal.initialize()
    return msal
  }

  function adopt(msalAccount) {
    if (!msalAccount) return null
    account = msalAccount
    return account
  }

  // BrowserAuthError included: the silent path broke rather than the credentials, and
  // only a redirect gets out of it.
  function needsInteraction(error) {
    if (InteractionRequiredAuthError && error instanceof InteractionRequiredAuthError) {
      return true
    }
    return Boolean(BrowserAuthError && error instanceof BrowserAuthError)
  }

  return {
    async initialize() {
      const instance = await ensureInstance()
      const response = await instance.handleRedirectPromise()
      if (response?.account) {
        adopt(response.account)
      } else {
        adopt(instance.getAllAccounts()[0] || null)
      }
      if (account) instance.setActiveAccount(account)
    },

    async signIn() {
      const instance = await ensureInstance()
      await instance.loginRedirect({ scopes, prompt: 'select_account' })
    },

    async signOut() {
      const instance = await ensureInstance()
      window.sessionStorage.removeItem(REAUTH_KEY)
      await instance.logoutRedirect({ account })
      account = null
    },

    async getToken() {
      if (!account) return null
      const instance = await ensureInstance()
      try {
        const result = await instance.acquireTokenSilent({
          scopes,
          account,
          cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        })
        window.sessionStorage.removeItem(REAUTH_KEY)
        return result.accessToken
      } catch (error) {
        if (!needsInteraction(error)) throw error
        if (window.sessionStorage.getItem(REAUTH_KEY)) {
          window.sessionStorage.removeItem(REAUTH_KEY)
          throw error
        }
        window.sessionStorage.setItem(REAUTH_KEY, '1')
        // No `prompt`, unlike signIn(): first-party session cookie, so a flash, not a
        // login screen.
        await instance.acquireTokenRedirect({ scopes, account })
        return null
      }
    },

    getAccount() {
      if (!account) return null
      return {
        username: (account.username || '').split('@')[0],
        name: account.name || account.username || '',
        upn: account.username || '',
      }
    },
  }
}

/**
 * The provider used when the Entra registration has not been wired up.
 *
 * It exists so a missing client ID produces one clear sentence on the sign-in screen
 * instead of a stack trace from inside MSAL — the registration has an external lead time
 * (see PLAN.md, "Blocked on someone else"), so an instance deployed without it is a case
 * that will happen. Signing in is the only thing it refuses, and it says why.
 *
 * @returns {AuthProvider}
 */
export function createUnconfiguredProvider() {
  return {
    async initialize() {},
    async signIn() {
      throw new Error(
        'Entra ID sign-in is not configured on this instance: ENTRA_CLIENT_ID and ENTRA_API_SCOPE are empty.',
      )
    },
    async signOut() {},
    async getToken() {
      return null
    },
    getAccount() {
      return null
    },
  }
}

/**
 * True when Entra ID has everything it needs to sign someone in. Read by the sign-in
 * screen, which shows an explanation instead of a button when it is false.
 *
 * @param {typeof config} [cfg]
 */
export function isProviderConfigured(cfg = config) {
  if (cfg.authDisabled) return true
  return Boolean(cfg.entra?.clientId)
}

/**
 * Pick the provider the configuration calls for: the stub when authentication is
 * switched off (impossible in a production build), Entra ID when it is registered, and
 * otherwise one that explains itself.
 *
 * @param {typeof config} [cfg]
 * @returns {AuthProvider}
 */
export function createAuthProvider(cfg = config) {
  if (cfg.authDisabled) return createStubProvider()
  if (!cfg.entra?.clientId) return createUnconfiguredProvider()
  return createMsalProvider(cfg)
}
